/**
 * ManualTemplatePicker — выбор шаблона для ручных рассылок
 * Дата: 26.02.2026 — PLAN Manual Broadcasts, Итерация 4
 *
 * Показывает шаблоны категории "manual" (и текстовые/WABA) с превью.
 * Используется в wizard ручных рассылок.
 */
import { useMemo } from 'react';
import type { Template } from '@/api/templates';
import { Select } from '@/components/ui/Select';
import {
  getCategoryLabel,
  getTechnicalTypeLabel,
  isManualBroadcastEligibleTemplate,
  isWabaBackedTemplate,
} from '@/features/templates/templateTaxonomy';
import { cn } from '@/utils/cn';

interface ManualTemplatePickerProps {
  templates: Template[];
  value: string;
  onChange: (templateId: string, template?: Template) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function ManualTemplatePicker({
  templates,
  value,
  onChange,
  placeholder = 'Выберите шаблон',
  disabled = false,
  className,
}: ManualTemplatePickerProps) {
  const manualTemplates = useMemo(
    () => templates.filter((t) => isManualBroadcastEligibleTemplate(t) && t.is_active),
    [templates]
  );

  const selectedTemplate = useMemo(
    () => (value ? manualTemplates.find((t) => t.id === value) : null),
    [value, manualTemplates]
  );

  return (
    <div className={cn('space-y-2', className)} data-testid="manual-template-picker">
      <Select
        value={value}
        onChange={(e) => {
          const id = e.target.value;
          const t = manualTemplates.find((x) => x.id === id);
          onChange(id, t);
        }}
        options={[
          { value: '', label: placeholder },
          ...manualTemplates.map((t) => ({
            value: t.id,
            label: `${t.name} (${getTechnicalTypeLabel(t)})`,
          })),
        ]}
        disabled={disabled}
      />
      {selectedTemplate && (
        <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
          <div className="font-medium text-neutral-900">{selectedTemplate.name}</div>
          {selectedTemplate.content && (
            <div className="mt-1 line-clamp-3 text-neutral-600">{selectedTemplate.content}</div>
          )}
          {selectedTemplate.variables?.length ? (
            <div className="mt-1 text-xs text-neutral-500">
              Переменные: {selectedTemplate.variables.map((v) => `{{${v}}}`).join(', ')}
            </div>
          ) : null}
          <div className="mt-1 flex flex-wrap gap-2 text-xs">
            <span className="inline-block text-blue-600">{getTechnicalTypeLabel(selectedTemplate)}</span>
            {selectedTemplate.category && (
              <span className="inline-block text-neutral-500">{getCategoryLabel(selectedTemplate.category, false)}</span>
            )}
            {isWabaBackedTemplate(selectedTemplate) && (
              <span className="inline-block text-blue-600">Meta/WABA</span>
            )}
          </div>
        </div>
      )}
      {manualTemplates.length === 0 && (
        <p className="text-sm text-amber-600">
          Нет шаблонов для ручных запусков. Подойдут активные шаблоны категории «Ручные» или маркетинговые шаблоны.
        </p>
      )}
    </div>
  );
}
